'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';

import { LayoutDashboard, ShoppingBag, Clock, GraduationCap, Wallet, Users, User, ChevronLeft, ChevronRight, LogOut, Trophy, Bell, Upload, Megaphone, ClipboardCheck, BarChart2, UserCog, Send } from 'lucide-react';
import { getCurrentUserProfile } from '@/lib/supabaseService';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
  adminOnly?: boolean;
}

interface SidebarProps {
  activeRoute: string;
  collapsed: boolean;
  onCollapsedChange: (v: boolean) => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export default function Sidebar({
  activeRoute,
  collapsed,
  onCollapsedChange,
  mobileOpen,
  onMobileClose,
}: SidebarProps) {
  const router = useRouter();
  const { user: authUser, signOut } = useAuth();
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    if (authUser) {
      getCurrentUserProfile().then((profile) => setUser(profile));
    }
  }, [authUser]);

  const memberNavItems: NavItem[] = [
    { label: 'Dashboard', href: '/dashboard', icon: <LayoutDashboard size={18} /> },
    { label: 'My Services', href: '/my-services', icon: <ShoppingBag size={18} /> },
    { label: 'Order History', href: '/order-history', icon: <Clock size={18} /> },
    { label: 'Notifications', href: '/notifications', icon: <Bell size={18} /> },
    { label: 'Training', href: '/training', icon: <GraduationCap size={18} /> },
    { label: 'Wallet', href: '/wallet', icon: <Wallet size={18} /> },
    { label: 'Affiliate', href: '/affiliate', icon: <Users size={18} /> },
    { label: 'Leaderboard', href: '/leaderboard', icon: <Trophy size={18} /> },
    { label: 'Profile & KYC', href: '/profile', icon: <User size={18} /> },
  ];

  const adminNavItems: NavItem[] = [
    { label: 'Members', href: '/admin', icon: <Users size={18} /> },
    { label: 'Training Video Upload', href: '/admin?tab=videos', icon: <Upload size={18} /> },
    { label: 'Announcement', href: '/admin?tab=announcements', icon: <Megaphone size={18} /> },
    { label: 'Pending Order Approval', href: '/admin?tab=verification', icon: <ClipboardCheck size={18} /> },
    { label: 'Analytics', href: '/admin?tab=analytics', icon: <BarChart2 size={18} /> },
    { label: 'Staff Management', href: '/admin?tab=staff', icon: <UserCog size={18} /> },
    { label: 'Pre-Launch Checklist', href: '/admin/checklist', icon: <ClipboardCheck size={18} /> },
    { label: 'Leaderboard', href: '/leaderboard', icon: <Trophy size={18} /> },
    { label: 'Profile & KYC', href: '/profile', icon: <User size={18} /> },
  ];

  const visibleItems = user?.isAdmin ? adminNavItems : memberNavItems;

  const handleLogout = async () => {
    await signOut();
    router.replace('/sign-up-login-screen');
  };

  const sidebarClasses = [
    'fixed top-0 left-0 h-full bg-card border-r border-border z-30 flex flex-col sidebar-transition',
    'lg:relative lg:translate-x-0',
    collapsed ? 'lg:w-16' : 'lg:w-60',
    mobileOpen ? 'translate-x-0 w-72 animate-slide-in-left' : '-translate-x-full lg:translate-x-0',
  ].join(' ');

  return (
    <aside className={sidebarClasses}>
      {/* Logo */}
      <div className={`flex items-center border-b border-border ${collapsed ? 'px-3 py-4 justify-center' : 'px-4 py-4 gap-2'}`}>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!collapsed && (
            <span className="text-[15px] font-bold tracking-tight whitespace-nowrap">
              <span className="text-foreground">dropservice</span>
              <span className="text-primary">.in</span>
            </span>
          )}
        </div>
        {/* Mobile close */}
        <button
          onClick={onMobileClose}
          className="ml-auto lg:hidden p-2.5 rounded-xl hover:bg-muted text-muted-foreground min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label="Close sidebar"
        >
          <ChevronLeft size={18} />
        </button>
      </div>

      {/* Nav items */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 scrollbar-thin">
        <div className="space-y-0.5">
          {visibleItems.map((item) => {
            const isActive = activeRoute === item.href || (activeRoute === '/' && item.href === '/dashboard');
            return (
              <Link
                key={`nav-${item.href}`}
                href={item.href === '/dashboard' ? '/' : item.href}
                onClick={onMobileClose}
                className={[
                  'flex items-center gap-3 px-3 py-3 lg:py-2 rounded-lg text-sm font-medium transition-all duration-150 group relative min-h-[44px]',
                  isActive
                    ? 'nav-item-active' :'text-muted-foreground nav-item-hover',
                ].join(' ')}
                title={collapsed ? item.label : undefined}
              >
                <span className="flex-shrink-0">{item.icon}</span>
                {!collapsed && (
                  <span className="truncate">{item.label}</span>
                )}
                {!collapsed && item.badge && item.badge > 0 && (
                  <span className="ml-auto bg-primary text-primary-foreground text-xs font-semibold px-1.5 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                )}
                {/* Collapsed tooltip */}
                {collapsed && (
                  <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                    {item.label}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Bottom: Telegram support + user + collapse */}
      <div className="border-t border-border p-2 space-y-1">
        {/* Telegram Support */}
        <a
          href="https://t.me/dropservicein"
          target="_blank"
          rel="noopener noreferrer"
          onClick={onMobileClose}
          className="flex items-center gap-3 w-full px-3 py-3 lg:py-2 rounded-lg text-sm font-medium text-[#229ED9] hover:bg-[#229ED9]/10 transition-all duration-150 group relative min-h-[44px]"
          title={collapsed ? 'Telegram Support' : undefined}
        >
          <Send size={18} className="flex-shrink-0" />
          {!collapsed && <span>Telegram Support</span>}
          {collapsed && (
            <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
              Telegram Support
            </span>
          )}
        </a>

        {!collapsed && user && (
          <div className="px-3 py-2 rounded-lg bg-muted/50">
            <p className="text-xs font-semibold text-foreground truncate">{user.fullName}</p>
            <p className="text-xs text-muted-foreground truncate">{user.email}</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-3 py-3 lg:py-2 rounded-lg text-sm font-medium text-muted-foreground nav-item-hover transition-all duration-150 group relative min-h-[44px]"
          title={collapsed ? 'Log out' : undefined}
        >
          <LogOut size={18} />
          {!collapsed && <span>Log out</span>}
          {collapsed && (
            <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
              Log out
            </span>
          )}
        </button>

        {/* Collapse toggle — desktop only */}
        <button
          onClick={() => onCollapsedChange(!collapsed)}
          className="hidden lg:flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground nav-item-hover transition-all duration-150"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          {!collapsed && <span className="text-xs">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}